let pending = Promise.resolve()
const blockedTokens = new Set()
export const resumePushSession = token => blockedTokens.delete(token)

export const notificationOrderId = value => {
    if (typeof value !== 'string') return null
    try {
        const url = new URL(value, window.location.origin)
        if (url.origin !== window.location.origin) return null
        const segments = url.pathname.split('/')
        if (segments.length === 3 && segments[1] === 'orders' && /^[0-9]+$/.test(segments[2])) return segments[2]
        const id = url.searchParams.get('orderId')
        return /^[0-9]+$/.test(id || '') ? id : null
    } catch { return null }
}

export const supportsPush = () => typeof window !== 'undefined' && 'Notification' in window
    && 'serviceWorker' in navigator && 'PushManager' in window

const exclusive = operation => {
    const run = () => navigator.locks
        ? navigator.locks.request('cofresh-push-binding', operation) : operation()
    const result = pending.then(run, run)
    pending = result.catch(() => {})
    return result
}

const isCurrent = token => token && !blockedTokens.has(token) && localStorage.getItem('token') === token

const request = async (url, token, body) => {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 10000)
    try {
        const response = await fetch(url, {
            method: body === undefined ? 'GET' : 'POST',
            headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
            ...(body === undefined ? {} : { body: JSON.stringify(body) }),
            signal: controller.signal,
        })
        const data = await response.json()
        if (!response.ok) throw new Error(response.status === 401 ? 'Войдите в аккаунт заново' : data.message || 'Не удалось подключить уведомления')
        return data
    } finally { clearTimeout(timeout) }
}

const toBytes = value => {
    const encoded = value.replace(/-/g, '+').replace(/_/g, '/')
    return Uint8Array.from(atob(encoded + '='.repeat((4 - encoded.length % 4) % 4)), character => character.charCodeAt(0))
}

const waitForWorker = async registration => {
    const worker = registration.installing || registration.waiting || registration.active
    if (!worker) throw new Error('Не удалось запустить уведомления в браузере')
    if (worker.state === 'activated') return worker
    return new Promise((resolve, reject) => {
        const finish = (error) => {
            clearTimeout(timeout)
            worker.removeEventListener('statechange', changed)
            if (error) reject(error)
            else resolve(worker)
        }
        const changed = () => {
            if (worker.state === 'activated') finish()
            else if (worker.state === 'redundant') finish(new Error('Обновите страницу и повторите подключение'))
        }
        const timeout = setTimeout(() => finish(new Error('Обновите страницу и повторите подключение')), 8000)
        worker.addEventListener('statechange', changed)
        changed()
    })
}

const tellWorker = async (registration, message) => {
    const worker = await waitForWorker(registration)
    return new Promise((resolve, reject) => {
        const channel = new MessageChannel()
        const finish = error => {
            clearTimeout(timeout)
            channel.port1.close()
            if (error) reject(error)
            else resolve()
        }
        const timeout = setTimeout(() => finish(new Error('Обновите страницу, чтобы подключить уведомления')), 5000)
        channel.port1.onmessage = event => finish(event.data?.ok ? null : new Error('Не удалось сохранить настройки уведомлений'))
        worker.postMessage(message, [channel.port2])
    })
}

export const connectPush = token => exclusive(async () => {
    if (!supportsPush() || Notification.permission !== 'granted' || !isCurrent(token)) return null
    const registration = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
    const user = JSON.parse(localStorage.getItem('user') || 'null')
    if (!isCurrent(token) || !user?.id) return null
    await tellWorker(registration, { type: 'PUSH_PREPARE_OWNER', userId: user.id })
    const { publicKey } = await request('/api/push/vapid-key')
    if (!publicKey) throw new Error('Уведомления временно недоступны на сервере')
    if (!isCurrent(token)) return null
    const key = toBytes(publicKey)
    let subscription = await registration.pushManager.getSubscription()
    const existingKey = subscription?.options?.applicationServerKey
    const changedKey = existingKey && Array.from(new Uint8Array(existingKey)).join(',') !== Array.from(key).join(',')
    if (subscription && (changedKey || (subscription.expirationTime && subscription.expirationTime <= Date.now()))) {
        await subscription.unsubscribe()
        subscription = null
    }
    if (!isCurrent(token)) return null
    await waitForWorker(registration)
    if (!subscription) subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
    if (!isCurrent(token)) return null
    const data = await request('/api/push/subscribe', token, subscription.toJSON())
    if (!data.success || !data.bindingId || !data.userId) throw new Error('Сервер не подтвердил подключение')
    if (!isCurrent(token)) return null
    await tellWorker(registration, { type: 'PUSH_SET_OWNER', owner: { userId: data.userId, bindingId: data.bindingId } })
    if (!isCurrent(token)) {
        await tellWorker(registration, { type: 'PUSH_CLEAR_OWNER', userId: data.userId })
        return null
    }
    return data
})

export const disconnectPush = (token, userId) => {
    if (!token || !supportsPush()) return Promise.resolve()
    blockedTokens.add(token)
    const supersededBySameUser = () => {
        const current = localStorage.getItem('token')
        try {
            return current && current !== token && Number(JSON.parse(localStorage.getItem('user'))?.id) === Number(userId)
        } catch { return false }
    }
    const clear = navigator.serviceWorker.getRegistration('/').then(registration => registration
        && !supersededBySameUser() ? tellWorker(registration, { type: 'PUSH_CLEAR_OWNER', userId }) : null).catch(() => {})
    return exclusive(async () => {
        await clear
        if (supersededBySameUser()) return
        const registration = await navigator.serviceWorker.getRegistration('/')
        const subscription = await registration?.pushManager.getSubscription()
        if (!subscription) return
        try { await request('/api/push/unsubscribe', token, { endpoint: subscription.endpoint }) }
        finally {
            const current = localStorage.getItem('token')
            if (!current || current === token) await subscription.unsubscribe()
        }
    })
}
